"use client";

import {
  type ComponentType,
  type ReactNode,
  type RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";
import {
  type SidekickConversationStarter,
  SidekickPanelContext,
} from "./sidekick-panel-context";

type SidekickPanelProviderShellProps = {
  children: ReactNode;
  orgSlug: string;
};

type SidekickPanelProps = {
  conversationStarterRef: RefObject<SidekickConversationStarter | null>;
  orgSlug: string;
};

type SidekickPanelTriggerProps = {
  hasUnreadNovedades?: boolean;
  orgSlug: string;
};

// Only the panel is lazy. The provider and wrappers render the same tree before
// and after it loads, so the page below keeps its state.
export function SidekickPanelProviderShell({
  children,
  orgSlug,
}: SidekickPanelProviderShellProps) {
  const [Panel, setPanel] = useState<ComponentType<SidekickPanelProps> | null>(
    null
  );
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const conversationStarterRef = useRef<SidekickConversationStarter | null>(
    null
  );
  const value = useMemo(
    () => ({
      open,
      setOpen,
      startConversationWith: (
        message: string,
        options?: { pageUrl?: string | null }
      ) => {
        flushSync(() => setOpen(true));
        return (
          conversationStarterRef.current?.startConversationWith(
            message,
            options
          ) ?? Promise.resolve({ accepted: false })
        );
      },
      toggleOpen: () => setOpen((current) => !current),
      triggerRef,
    }),
    [open]
  );

  useEffect(() => {
    let mounted = true;

    void import("./sidekick-panel-provider").then((module) => {
      if (mounted) {
        setPanel(() => module.SidekickPanel);
      }
    });

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <SidekickPanelContext.Provider value={Panel ? value : null}>
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">{children}</div>
        {Panel ? (
          <Panel
            conversationStarterRef={conversationStarterRef}
            orgSlug={orgSlug}
          />
        ) : null}
      </div>
    </SidekickPanelContext.Provider>
  );
}

export function SidekickPanelTriggerShell(props: SidekickPanelTriggerProps) {
  const [Trigger, setTrigger] =
    useState<ComponentType<SidekickPanelTriggerProps> | null>(null);

  useEffect(() => {
    let mounted = true;

    void import("./sidekick-panel-provider").then((module) => {
      if (mounted) {
        setTrigger(() => module.SidekickPanelTrigger);
      }
    });

    return () => {
      mounted = false;
    };
  }, []);

  if (!Trigger) {
    return null;
  }

  return <Trigger {...props} />;
}
